/**
 * useVersionWithStorageLimit Hook
 *
 * Wrapper hook that enforces storage limit checks before version uploads.
 * This centralizes the paywall logic to prevent bypassing limit checks.
 *
 * Modeled after useCreateTableWithLimit for consistency.
 *
 * Usage:
 * ```tsx
 * const { checkStorageBeforeUpload, StorageLimitDialog } = useVersionWithStorageLimit();
 *
 * const handleUpload = async (file: File) => {
 *   const canProceed = await checkStorageBeforeUpload(file.size);
 *   if (!canProceed) return; // Dialog will be shown automatically
 *
 *   // Proceed with upload...
 * };
 *
 * return (
 *   <>
 *     <UploadButton onClick={() => handleUpload(file)} />
 *     <StorageLimitDialog />
 *   </>
 * );
 * ```
 */

import { useState, useCallback } from 'react';

import { UpgradeDialog } from '@/components/billing';
import { useRpc } from '@/lib/rpc';
import { useWorkspaceStore } from '@/store/useWorkspaceStore';

interface StorageData {
  usage: number;
  limit: number | null;
  available: number | null;
  percentUsed: number;
}

/**
 * Hook that wraps version upload with storage limit enforcement
 *
 * @returns Object containing:
 * - checkStorageBeforeUpload: Function to check storage before upload (returns boolean)
 * - StorageLimitDialog: Component to render the upgrade dialog
 * - isChecking: Whether storage check is in progress
 */
export function useVersionWithStorageLimit() {
  const rpc = useRpc();
  const { selectedWorkspaceId } = useWorkspaceStore();

  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [storageData, setStorageData] = useState<StorageData | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  /**
   * Check storage capacity before upload
   * Shows upgrade dialog if limit is reached
   *
   * @param fileSize - File size in bytes
   * @returns True if upload can proceed, false if limit reached (dialog will be shown)
   */
  const checkStorageBeforeUpload = useCallback(
    async (fileSize: number): Promise<boolean> => {
      setIsChecking(true);

      try {
        // GET /api/private/workspaces/check-storage?fileSize=...
        // @ts-expect-error - Hono RPC type inference limitation for nested routes
        const res = await rpc.api.private.workspaces['check-storage'].$get({
          query: { fileSize: fileSize.toString() },
        });

        if (!res.ok) {
          // If check fails, allow upload (fail open)
          console.warn('Storage check failed, allowing upload');
          return true;
        }

        const response = await res.json();

        // Check for error response
        if ('error' in response) {
          console.warn('Storage check error response, allowing upload');
          return true;
        }

        const { can_create_version, current_usage, limit, available, percent_used } = response;

        // Store storage data for dialog
        setStorageData({
          usage: current_usage,
          limit,
          available,
          percentUsed: percent_used,
        });

        // If version not allowed and there's a limit, show dialog
        if (!can_create_version && limit !== null) {
          setShowLimitDialog(true);
          return false;
        }

        return true;
      } catch (error) {
        console.error('Error checking storage:', error);
        // Fail open - allow upload if check errors
        return true;
      } finally {
        setIsChecking(false);
      }
    },
    [rpc]
  );

  /**
   * Render the storage limit dialog component
   * Must be included in the component tree
   */
  const StorageLimitDialog = useCallback(() => {
    if (!showLimitDialog) return null;

    // No storage data or no limit - shouldn't happen but handle gracefully
    if (!storageData || storageData.limit === null) {
      return null;
    }

    return (
      <UpgradeDialog
        context="storage_limit"
        open={showLimitDialog}
        onOpenChange={setShowLimitDialog}
        storageUsage={storageData.usage}
        storageLimit={storageData.limit}
        workspaceId={selectedWorkspaceId || ''}
      />
    );
  }, [showLimitDialog, storageData, selectedWorkspaceId]);

  return {
    checkStorageBeforeUpload,
    StorageLimitDialog,
    isChecking,
    // Expose raw state for advanced use cases
    showLimitDialog,
    setShowLimitDialog,
    storageData,
  };
}
